import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { adaptEchartsOptions, BaseEchartsDirective } from '@examdojo/core/widget';
import type { EChartsOption } from 'echarts';
import { BarChart, LineChart, ScatterChart } from 'echarts/charts';
import { GridComponent, MarkLineComponent, TitleComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';

echarts.use([LineChart, BarChart, ScatterChart, GridComponent, MarkLineComponent, TitleComponent]);

import type { TestMeasureId } from '../../measures/test-measures/test-measures.model';
import type { TestResultOrError } from './test-runner.model';
import { isTestError } from './test-runner.model';
import { buildTestResultChartOptions } from './test-result-chart.util';

@Component({
  selector: 'dojo-test-runner',
  standalone: true,
  imports: [NgxEchartsDirective],
  providers: [provideEchartsCore({ echarts })],
  templateUrl: './test-runner.component.html',
  styleUrl: './test-runner.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestRunnerComponent extends BaseEchartsDirective {
  readonly result = input.required<TestResultOrError>();
  readonly highlightedMeasure = input<TestMeasureId | null>(null);

  readonly isError = computed(() => isTestError(this.result()));

  readonly errorMessage = computed(() => {
    const r = this.result();
    return isTestError(r) ? r.error : null;
  });

  readonly testResult = computed(() => {
    const r = this.result();
    return isTestError(r) ? null : r;
  });

  readonly chartOptions = computed<EChartsOption | null>(() => {
    const r = this.testResult();
    const highlight = this.highlightedMeasure();
    return r ? buildTestResultChartOptions(r, highlight) : null;
  });

  readonly adaptedOptions = computed<EChartsOption | null>(() => {
    const raw = this.chartOptions();
    return raw ? adaptEchartsOptions(raw, this.document, this.isDark()) : null;
  });
}
